import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import ReactSignatureCanvas from 'react-signature-canvas';
import ClientOnboardingApi from '../../../../apis/ClientOnboardingApi';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import useApi from '../../../../lib/useApi';
import CommonStepProps from '../../lib/CommonStepProps';

/**
 * A step that presents a user with a docusign and the generated master service agreement
 */
const MasterServiceAgreementStep = ({
  isStepComplete,
  setStepComplete,
  moveToNextStep,
  showError,
  hideTitle,
}: CommonStepProps) => {
  const theme = useTheme();
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const [pdfPath, setPdfPath] = useState('');
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [isSigned, setIsSigned] = useState(isStepComplete);
  const sigRef = useRef<ReactSignatureCanvas | null>(null);
  const snackbarContext = useContext(SnackbarContext);
  const [isSigning, setIsSigning] = useState(false);

  useEffect(() => {
    hideTitle();
  }, [hideTitle]);

  useEffect(() => {
    clientOnboardingApi
      .GetMasterServiceAgreementPdf()
      .then((s) => {
        setPdfPath(s);
      })
      .catch(() => showError());
  }, [clientOnboardingApi, isStepComplete, showError]);

  return (
    <Box height="100%" overflow="hidden">
      <Grid container sx={{ height: '100%', overflow: 'auto' }} spacing={1}>
        <Grid
          item
          container
          xs={12}
          lg={3}
          spacing={1}
          sx={{
            maxHeight: '100%',
            overflow: 'auto',
          }}
        >
          <Grid item xs={12}>
            <Typography
              component="h1"
              textAlign="center"
              fontSize="2.5em"
              fontWeight="600"
            >
              Master Service Agreement
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={12}>
            <Typography variant="h5" gutterBottom>
              Review and Sign
            </Typography>
            <Typography sx={{ marginBottom: '1em' }}>
              We&apos;re excited to present this Master Service Agreement for
              the S3G Security training. Please review the content and sign
              using the button below.
            </Typography>
            <Box display="flex" justifyContent="center">
              <Tooltip title={isStepComplete ? 'Already signed' : ''} arrow>
                <span
                  style={{
                    cursor: isStepComplete ? 'help' : 'help',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => setSignDialogOpen(true)}
                    endIcon={<EditOutlinedIcon />}
                    disabled={isStepComplete}
                  >
                    Sign Master Service Agreement
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={12}>
            <Box display="flex" justifyContent="center" marginBottom="1em">
              <Tooltip
                title={isSigned ? '' : 'Please sign before continuing'}
                arrow
              >
                <span
                  style={{
                    cursor: isSigned ? 'initial' : 'help',
                  }}
                >
                  <Button
                    disabled={!isSigned}
                    variant="contained"
                    onClick={moveToNextStep}
                  >
                    Next Step
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={9} height="100%">
          {pdfPath === '' ? (
            <Box
              height="100%"
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="1.5em"
            >
              <CircularProgress thickness={5} />
              <Typography fontSize="1.8em">Loading...</Typography>
            </Box>
          ) : (
            <Box display="flex" height="100%" gap="1em">
              <iframe
                src={pdfPath}
                height={300}
                style={{
                  borderColor: theme.palette.secondary.main,
                  borderWidth: '1px',
                  borderStyle: theme.palette.mode == 'dark' ? 'none' : 'solid',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Dialog
        open={signDialogOpen}
        onClose={(_, reason) => {
          // Prevent the backdrop click from closing the dialog while the signing is occurring
          if (isSigning && reason == 'backdropClick') return;

          setSignDialogOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Sign Master Service Agreement</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', gap: '1em', flexDirection: 'column' }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography>
              Please provide your signature in the box below.
            </Typography>
            <Tooltip title="Clear Signature" arrow placement="left">
              <IconButton onClick={() => sigRef.current?.clear()}>
                <CancelPresentationRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '200px',
            }}
          >
            <SignatureCanvas
              canvasProps={{
                style: {
                  height: '100%',
                  width: '100%',
                  borderColor: theme.palette.secondary.main,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                },
              }}
              backgroundColor="white"
              ref={(r) => (sigRef.current = r)}
            />
          </Box>
          <Box display="flex" gap=".5em" justifyContent="right">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setSignDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Get the canvas
                const trimmedCanvas = sigRef.current?.getTrimmedCanvas();

                // Sign the master service agreement
                trimmedCanvas?.toBlob((b) => {
                  if (b == null || sigRef.current?.isEmpty()) return;

                  setIsSigning(true);
                  clientOnboardingApi
                    .SignMasterServiceAgreementPdf(b)
                    .then((p) => {
                      setPdfPath(p);
                      setStepComplete();
                      setIsSigned(true);
                      setSignDialogOpen(false);
                      snackbarContext.setAlert(
                        <Alert variant="filled" severity="success">
                          Successfully signed the master service agreement
                        </Alert>
                      );
                      snackbarContext.triggerSnackbar();
                    })
                    .catch((e: Error) => {
                      snackbarContext.setAlert(
                        <Alert variant="filled" severity="error">
                          {e.message !== ''
                            ? e.message
                            : 'An error occurred while signing the master service agreement. Please try again.'}
                        </Alert>
                      );
                      snackbarContext.triggerSnackbar();
                    })
                    .finally(() => setIsSigning(false));
                });
              }}
              endIcon={<EditOutlinedIcon />}
            >
              Sign
            </Button>
          </Box>
        </DialogContent>
        <LoadingCircle isLoading={isSigning} loadingText="Signing..." />
      </Dialog>
    </Box>
  );
};

export default MasterServiceAgreementStep;
